import {
  ComponentLayoutThumbnail,
  ComponentOverlayLayer,
  type ExpectedComponent,
  TimingThumbnail,
} from '@cx-web/components/page-template/ComponentOverlay';
import { FrameworkOverlay } from '@cx-web/components/page-template/FrameworkOverlay';
import {
  type AdUnitConfiguration,
  SKYSCRAPER_LEFT_SLOT_ID,
  SKYSCRAPER_RIGHT_SLOT_ID,
} from '@cx-web/feature-gam-advertising';
import clsx from 'clsx';

import AdSlot from '~/components/composites/ads/AdSlot';
import { type ComponentLoaderContext } from '~/types';
import { type DynamicPageSectionName, type SectionMap } from '~/types/template';

import { getDynamicClasses, showPageSections } from '../../utils';
import { renderComponents } from '../../utils/renderComponents';
import { Section } from '../Section/Section';

import styles from './DynamicPageTemplate.module.css';

interface DynamicPageTemplateProps {
  adUnitConfiguration?: AdUnitConfiguration[];
  componentLoaderContext: ComponentLoaderContext;
  expectedComponentLayout?: ExpectedComponent[];
  gtmId: string;
  hasBannerAds: boolean;
  sections: Pick<SectionMap, DynamicPageSectionName>;
  templateId?: string;
  templateName?: string;
  timings?: Record<string, number>;
}

export const DynamicPageTemplate = ({
  adUnitConfiguration = [],
  componentLoaderContext,
  expectedComponentLayout,
  gtmId,
  hasBannerAds,
  sections,
  templateId,
  templateName,
  timings,
}: DynamicPageTemplateProps) => {
  const { showBottom, showCenterBottom, showCenterTop, showRight, showTop } = showPageSections(sections);

  const { bottom, centerBottom, centerTop, contentContainer, mainWrapper, right, top } = getDynamicClasses(sections);

  const containerClasses = clsx('adverts-and-atlas-wrapper', {
    'adverts-and-atlas-wrapper--adless': !hasBannerAds,
  });

  return (
    <div className={containerClasses}>
      <main className={`${styles.mainWrapper} ${mainWrapper}`} data-overlay-root id="dynamic-page-main-tag-wrapper">
        {hasBannerAds && (
          <div className="ad-left-container">
            <div id={SKYSCRAPER_LEFT_SLOT_ID}></div>
          </div>
        )}
        <Section
          ariaLabel="Page Header Top Section"
          className={`${styles.dynamicPageTop} ${top}`}
          content={renderComponents({
            components: sections.top ?? null,
            componentLoaderContext,
            gtmId,
            section: 'Top',
          })}
          dataTestId="dynamic-page-top"
          id="dynamic-page-top"
          show={showTop}
        />
        <div
          className={`${styles.dynamicPageContentContainer} ${showRight ? styles.standardWithRight : styles.standard} ${contentContainer}`}
          id="dynamic-page-content-container"
        >
          <>
            <Section
              ariaLabel="Main Content Center Top section"
              className={`${styles.dynamicPageCenterTop} ${centerTop}`}
              content={
                <>
                  {/* No banner on an empty centerTop — Section always renders `content` regardless
                      of `show`, and the unshown state is `display: none`, so an unconditional
                      AdSlot here would still call googletag.display() into an invisible div. */}
                  {showCenterTop && <AdSlot adUnitConfiguration={adUnitConfiguration} placement="inline-banner" />}
                  {renderComponents({
                    components: sections.centerTop ?? null,
                    componentLoaderContext,
                    gtmId,
                    section: 'Center Top',
                  })}
                </>
              }
              dataTestId="dynamic-page-center-top"
              id="dynamic-page-center-top"
              show={showCenterTop}
            />
          </>
          <>
            <Section
              ariaLabel="Secondary Content Right Section"
              className={`${styles.dynamicPageRight} ${right}`}
              content={
                <>
                  {renderComponents({
                    components: sections.right ?? null,
                    componentLoaderContext,
                    gtmId,
                    section: 'Right',
                  })}
                  {/* Right rail only — no MPU on single-column pages (no `sections.right` to show). */}
                  {showRight && <AdSlot adUnitConfiguration={adUnitConfiguration} placement="mpu" />}
                </>
              }
              dataTestId="dynamic-page-right"
              id="dynamic-page-right"
              show={showRight}
            />
          </>
          <Section
            ariaLabel="Main Content Center Bottom Section"
            className={`${styles.dynamicPageCenterBottom} ${centerBottom}`}
            content={renderComponents({
              components: sections.centerBottom ?? null,
              componentLoaderContext,
              gtmId,
              section: 'Center Bottom',
            })}
            dataTestId="dynamic-page-center-bottom"
            id="dynamic-page-center-bottom"
            show={showCenterBottom}
          />
        </div>
        <Section
          ariaLabel="Informational Content Bottom Section"
          className={`${styles.dynamicPageBottom} ${bottom}`}
          content={renderComponents({
            components: sections.bottom ?? null,
            componentLoaderContext,
            gtmId,
            section: 'Bottom',
          })}
          dataTestId="dynamic-page-bottom"
          id="dynamic-page-bottom"
          show={showBottom}
        />
        {hasBannerAds && (
          <div className="ad-right-container">
            <div id={SKYSCRAPER_RIGHT_SLOT_ID}></div>
          </div>
        )}
        {componentLoaderContext.showFrameworkOverlay && <FrameworkOverlay framework="RRv7" />}
        {componentLoaderContext.showComponentOverlay && <ComponentOverlayLayer />}
        {componentLoaderContext.showComponentOverlay && expectedComponentLayout && (
          <ComponentLayoutThumbnail
            expectedLayout={expectedComponentLayout}
            templateId={templateId}
            templateName={templateName}
          />
        )}
        {timings && <TimingThumbnail timings={timings} />}
      </main>
    </div>
  );
};
